import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';

const ArtemisOverview = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const optimizationProcessRef = useRef(null);

  const downloadOptimizationProcessAsPNG = async () => {
    if (optimizationProcessRef.current) {
      try {
        const canvas = await html2canvas(optimizationProcessRef.current, {
          backgroundColor: '#0F2027',
          scale: 2,
          logging: false,
          useCORS: true
        });
        
        const link = document.createElement('a');
        link.download = 'artemis-optimization-process.png';
        link.href = canvas.toDataURL();
        link.click();
      } catch (error) {
        console.error('Error generating PNG:', error);
      }
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
      color: 'white',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ 
            margin: '0 0 15px 0', 
            fontSize: '48px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            ⚡ What is Artemis?
          </h1>
          <p style={{ 
            margin: 0, 
            fontSize: '24px', 
            color: '#94a3b8',
            fontWeight: '500'
          }}>
            AI-Powered Code Evolution Platform
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '20px',
          marginBottom: '40px',
          borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
          paddingBottom: '0',
          justifyContent: 'center'
        }}>
          <button
            onClick={() => setActiveTab('overview')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'overview' ? '#6366f1' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'overview' ? '3px solid #6366f1' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            What is Artemis
          </button>
          <button
            onClick={() => setActiveTab('process')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'process' ? '#f59e0b' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'process' ? '3px solid #f59e0b' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            How It Works
          </button>
          <button
            onClick={() => setActiveTab('examples')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'examples' ? '#10b981' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'examples' ? '3px solid #10b981' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            Live Examples
          </button>
          <button
            onClick={() => setActiveTab('scoring')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'scoring' ? '#8b5cf6' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'scoring' ? '3px solid #8b5cf6' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            Scoring System
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', gap: '40px' }}>
            {/* Main Content */}
            <div style={{ flex: 1 }}>
              {/* Before/After Code Evolution Flow */}
              <div style={{ maxWidth: '1000px' }}>
                {/* Step 1: Your Code Repository */}
                <div style={{ marginBottom: '50px' }}>
                  <div style={{ 
                    background: 'rgba(100, 116, 139, 0.2)',
                    border: '2px solid #475569',
                    borderRadius: '16px',
                    padding: '30px',
                    position: 'relative'
                  }}>
                    <div style={{ 
                      position: 'absolute',
                      top: '-15px',
                      left: '30px',
                      background: '#475569',
                      padding: '8px 20px',
                      borderRadius: '20px',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}>
                      📂 BEFORE: Your Code Repository
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '30px', 
                      marginTop: '20px' 
                    }}>
                      <div>
                        <h4 style={{ color: '#f87171', marginBottom: '15px' }}>❌ Current Issues:</h4>
                        <div style={{ fontSize: '16px', lineHeight: '1.8', color: '#cbd5e1' }}>
                          • Slow database queries (2.5s response)<br/>
                          • Memory leaks in data processing<br/>
                          • Complex nested loops<br/>
                          • Poor error handling<br/>
                          • Duplicate code patterns
                        </div>
                      </div>
                      <div>
                        <h4 style={{ color: '#94a3b8', marginBottom: '15px' }}>📝 Sample Code:</h4>
                        <div style={{
                          background: '#1e293b',
                          padding: '20px',
                          borderRadius: '8px',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                          border: '1px solid #334155'
                        }}>
                          <pre style={{ margin: 0, color: '#e2e8f0' }}>{`def process_users(data):
  results = []
  for user in data:
    for item in user.items:
      # Slow DB call
      details = db.get(item.id)
      results.append(details)
  return results`}</pre>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Evolution Arrow */}
                <div style={{ textAlign: 'center', marginBottom: '50px' }}>
                  <div style={{
                    fontSize: '48px',
                    color: '#6366f1',
                    marginBottom: '10px'
                  }}>
                    ↓
                  </div>
                  <div style={{
                    fontSize: '20px',
                    color: '#6366f1',
                    fontWeight: '600'
                  }}>
                    🧬 ARTEMIS EVOLUTION
                  </div>
                </div>

                {/* Multi-Objective Results */}
                <div style={{ marginBottom: '40px' }}>
                  <div style={{ 
                    background: 'rgba(16, 185, 129, 0.2)',
                    border: '2px solid #10b981',
                    borderRadius: '16px',
                    padding: '30px',
                    position: 'relative'
                  }}>
                    <div style={{ 
                      position: 'absolute',
                      top: '-15px',
                      left: '30px',
                      background: '#10b981',
                      padding: '8px 20px',
                      borderRadius: '20px',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}>
                      🎯 AFTER: Multi-Objective Results - You Decide
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '30px',
                      marginTop: '20px'
                    }}>
                      {/* Agent A Result */}
                      <div style={{
                        background: 'rgba(139, 92, 246, 0.3)',
                        border: '2px solid #8b5cf6',
                        borderRadius: '12px',
                        padding: '20px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: '15px'
                        }}>
                          <div style={{ fontSize: '24px', marginRight: '10px' }}>🤖</div>
                          <h4 style={{ color: '#c4b5fd', margin: 0 }}>Agent A: Performance Focus</h4>
                        </div>
                        
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{
                            background: '#1e293b',
                            padding: '15px',
                            borderRadius: '8px',
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            border: '1px solid #8b5cf6'
                          }}>
                            <pre style={{ margin: 0, color: '#e2e8f0' }}>{`def process_users_fast(data):
  # Batch processing + caching
  ids = [i.id for u in data for i in u.items]
  details = db.batch_get(ids, cache=True)
  return details`}</pre>
                          </div>
                        </div>
                        
                        <div style={{ fontSize: '14px', color: '#d1d5db' }}>
                          <div style={{ color: '#34d399' }}>⚡ Speed: 2.5s → 89ms (96% faster)</div>
                          <div style={{ color: '#fbbf24' }}>💾 Memory: 512MB → 64MB</div>
                          <div style={{ color: '#f87171' }}>📖 Readability: 6/10</div>
                        </div>
                      </div>

                      {/* Agent B Result */}
                      <div style={{
                        background: 'rgba(245, 158, 11, 0.3)',
                        border: '2px solid #f59e0b',
                        borderRadius: '12px',
                        padding: '20px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: '15px'
                        }}>
                          <div style={{ fontSize: '24px', marginRight: '10px' }}>🤖</div>
                          <h4 style={{ color: '#fde68a', margin: 0 }}>Agent B: Balanced</h4>
                        </div>
                        
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{
                            background: '#1e293b',
                            padding: '15px',
                            borderRadius: '8px',
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            border: '1px solid #f59e0b'
                          }}>
                            <pre style={{ margin: 0, color: '#e2e8f0' }}>{`def process_users_balanced(data):
  # Clean, fast, maintainable
  user_items = flatten([u.items for u in data])
  return fetch_details_batch(user_items)`}</pre>
                          </div>
                        </div>
                        
                        <div style={{ fontSize: '14px', color: '#d1d5db' }}>
                          <div style={{ color: '#34d399' }}>⚡ Speed: 2.5s → 145ms (94% faster)</div>
                          <div style={{ color: '#fbbf24' }}>💾 Memory: 512MB → 128MB</div>
                          <div style={{ color: '#34d399' }}>📖 Readability: 9/10</div>
                        </div>
                      </div>
                    </div>

                    {/* Decision Point */}
                    <div style={{
                      marginTop: '20px',
                      padding: '20px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '12px',
                      border: '2px dashed #6b7280',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '20px', marginBottom: '10px' }}>👥</div>
                      <h4 style={{ color: '#fbbf24', margin: '0 0 10px 0' }}>Your Choice</h4>
                      <p style={{ margin: 0, fontSize: '16px', color: '#d1d5db' }}>
                        Maximum speed? Choose Agent A. Balanced maintainability? Choose Agent B.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div style={{
              width: '320px',
              display: 'flex',
              flexDirection: 'column',
              gap: '25px'
            }}>
              {/* Why Artemis */}
              <div style={{
                background: 'rgba(99, 102, 241, 0.1)',
                border: '1px solid rgba(99, 102, 241, 0.3)',
                borderRadius: '16px',
                padding: '20px'
              }}>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#6366f1'
                }}>
                  🎯 Why Artemis?
                </h3>
                <div style={{ fontSize: '14px', color: '#e2e8f0', lineHeight: '1.7' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ color: '#60a5fa' }}>⚡ 10x Faster:</strong><br/>
                    Automate weeks of optimization
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ color: '#34d399' }}>🎯 Better Results:</strong><br/>
                    AI finds hidden optimizations
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ color: '#fbbf24' }}>✅ Proven Quality:</strong><br/>
                    Every suggestion tested
                  </div>
                  <div>
                    <strong style={{ color: '#a78bfa' }}>🧬 Always Learning:</strong><br/>
                    Gets smarter over time
                  </div>
                </div>
              </div>

              {/* Perfect For */}
              <div style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '16px',
                padding: '20px'
              }}>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#10b981'
                }}>
                  🚀 Perfect For
                </h3>
                <div style={{ fontSize: '13px', color: '#e2e8f0', lineHeight: '1.6' }}>
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ color: '#fbbf24' }}>🏢 Enterprise:</strong> Legacy modernization
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ color: '#f87171' }}>⚡ Performance:</strong> Critical bottlenecks
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ color: '#8b5cf6' }}>🔄 Continuous:</strong> Ongoing evolution
                  </div>
                  <div>
                    <strong style={{ color: '#06b6d4' }}>👥 Productivity:</strong> Focus on features
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'process' && (
          <div>
            {/* Download Button */}
            <div style={{ textAlign: 'right', marginBottom: '20px' }}>
              <button
                onClick={downloadOptimizationProcessAsPNG}
                style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }}
              >
                📥 Download as PNG
              </button>
            </div>
            
            {/* Evolution Process Overview */}
            <div ref={optimizationProcessRef} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '40px',
              marginBottom: '40px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h2 style={{ margin: '0 0 30px 0', fontSize: '28px', textAlign: 'center' }}>
                The Artemis Optimization Process
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '30px'
              }}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3498DB, #2980B9)',
                    margin: '0 auto 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '36px'
                  }}>
                    1
                  </div>
                  <h3 style={{ margin: '0 0 10px 0', color: '#3498DB' }}>Analyze</h3>
                  <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                    Artemis scans the entire codebase to identify optimization opportunities
                  </p>
                </div>

                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                    margin: '0 auto 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '36px'
                  }}>
                    2
                  </div>
                  <h3 style={{ margin: '0 0 10px 0', color: '#E74C3C' }}>Generate</h3>
                  <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                    Multiple variations are created using different LLMs and approaches
                  </p>
                </div>

                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #27AE60, #229954)',
                    margin: '0 auto 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '36px'
                  }}>
                    3
                  </div>
                  <h3 style={{ margin: '0 0 10px 0', color: '#27AE60' }}>Evaluate</h3>
                  <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                    Each variation is scored on performance, readability, and correctness
                  </p>
                </div>

                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #9B59B6, #8E44AD)',
                    margin: '0 auto 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '36px'
                  }}>
                    4
                  </div>
                  <h3 style={{ margin: '0 0 10px 0', color: '#9B59B6' }}>Optimize</h3>
                  <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                    The best combination across the entire codebase is presented as options
                  </p>
                </div>
              </div>
            </div>

            {/* Key Features Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: '30px'
            }}>
              <div style={{
                background: 'rgba(52, 152, 219, 0.1)',
                borderRadius: '16px',
                padding: '30px',
                border: '1px solid rgba(52, 152, 219, 0.3)'
              }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#3498DB' }}>
                  🔍 Whole Repository Analysis
                </h3>
                <p style={{ margin: 0, lineHeight: '1.8' }}>
                  Unlike traditional tools that optimize file-by-file, Artemis analyzes the entire 
                  codebase to find the best global optimization strategy. It considers interactions 
                  between components and finds the optimal combination of changes.
                </p>
              </div>

              <div style={{
                background: 'rgba(231, 76, 60, 0.1)',
                borderRadius: '16px',
                padding: '30px',
                border: '1px solid rgba(231, 76, 60, 0.3)'
              }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#E74C3C' }}>
                  🧬 Multi-LLM Evolution
                </h3>
                <p style={{ margin: 0, lineHeight: '1.8' }}>
                  Multiple AI models (GPT-4, Claude, Gemini) work together to generate variations. 
                  Evolutionary algorithms test different combinations to find configurations 
                  that perform best across all metrics.
                </p>
              </div>

              <div style={{
                background: 'rgba(39, 174, 96, 0.1)',
                borderRadius: '16px',
                padding: '30px',
                border: '1px solid rgba(39, 174, 96, 0.3)'
              }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#27AE60' }}>
                  🔄 Auto-Feedback Loop
                </h3>
                <p style={{ margin: 0, lineHeight: '1.8' }}>
                  Human feedback ("make it faster"), system metrics (89ms response), and AI analysis 
                  are automatically converted into quantitative objectives that guide the optimization.
                </p>
              </div>

              <div style={{
                background: 'rgba(155, 89, 182, 0.1)',
                borderRadius: '16px',
                padding: '30px',
                border: '1px solid rgba(155, 89, 182, 0.3)'
              }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#9B59B6' }}>
                  🎯 Multi-Objective Results
                </h3>
                <p style={{ margin: 0, lineHeight: '1.8' }}>
                  Instead of one "best" solution, Artemis presents multiple optimized options 
                  with different trade-offs. You choose based on your priorities: speed, 
                  maintainability, memory usage, or readability.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'examples' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '40px',
              marginBottom: '30px'
            }}>
              <h2 style={{ margin: '0 0 10px 0', fontSize: '32px', color: '#10b981', textAlign: 'center' }}>
                Live Optimization Examples
              </h2>
              <p style={{ margin: '0 0 30px 0', fontSize: '18px', opacity: 0.8, textAlign: 'center' }}>
                Real before/after code transformations with performance improvements
              </p>

              {/* Example: Data Structure Optimization */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '12px',
                padding: '30px',
                marginBottom: '30px'
              }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#3498DB' }}>
                  Example 1: Customer Order Processing System
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '30px'
                }}>
                  {/* Before */}
                  <div>
                    <h4 style={{ color: '#E74C3C', marginBottom: '15px' }}>❌ Before Optimization:</h4>
                    <pre style={{
                      background: 'rgba(0, 0, 0, 0.5)',
                      padding: '20px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      overflow: 'auto',
                      border: '1px solid rgba(231, 76, 60, 0.3)',
                      color: '#f8f8f2'
                    }}>{`class OrderProcessor:
    def __init__(self):
        # Using LinkedList for order queue
        self.pending_orders = LinkedList()
        # Using List for customer lookup
        self.customers = []
        
    def process_next_order(self):
        order = self.pending_orders.remove_first()
        # O(n) customer lookup
        customer = next(c for c in self.customers 
                       if c.id == order.customer_id)
        # Process order...
        
    def get_customer_history(self, customer_id):
        # O(n) search through entire history
        return [o for o in self.processed_orders 
                if o.customer_id == customer_id]

# Performance: 
# - 100 orders/sec with 1000 customers
# - 500ms avg response time`}</pre>
                  </div>

                  {/* After */}
                  <div>
                    <h4 style={{ color: '#27AE60', marginBottom: '15px' }}>✅ After Artemis Optimization:</h4>
                    <pre style={{
                      background: 'rgba(0, 0, 0, 0.5)',
                      padding: '20px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      overflow: 'auto',
                      border: '2px solid rgba(39, 174, 96, 0.3)',
                      color: '#f8f8f2'
                    }}>{`class OrderProcessor:
    def __init__(self):
        # Using Deque for efficient queue operations
        self.pending_orders = collections.deque()
        # Using HashMap for O(1) lookups
        self.customers = {}
        # New: Index for customer order history
        self.customer_order_index = defaultdict(list)
        
    def process_next_order(self):
        order = self.pending_orders.popleft()
        # O(1) customer lookup
        customer = self.customers[order.customer_id]
        # Process order...
        # Update index for fast history retrieval
        self.customer_order_index[order.customer_id].append(order)
        
    def get_customer_history(self, customer_id):
        # O(1) indexed lookup instead of O(n) search
        return self.customer_order_index[customer_id]

# Performance: 
# - 940 orders/sec (9.4x faster!)
# - 53ms avg response time`}</pre>
                  </div>
                </div>

                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  borderRadius: '8px',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}>
                  <strong style={{ color: '#34d399' }}>Key Improvements:</strong>
                  <div style={{ marginTop: '10px', fontSize: '14px' }}>
                    • Replaced LinkedList with Deque for better queue operations<br/>
                    • Added HashMap for O(1) customer lookups instead of O(n) linear search<br/>
                    • Introduced indexing for customer history to eliminate O(n) filtering<br/>
                    • Result: <strong style={{ color: '#fbbf24' }}>9.4x performance improvement</strong>
                  </div>
                </div>
              </div>

              {/* Example 2: Function Optimization */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '12px',
                padding: '30px'
              }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#F39C12' }}>
                  Example 2: Matrix Computation Optimization
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '30px'
                }}>
                  {/* Before */}
                  <div>
                    <h4 style={{ color: '#E74C3C', marginBottom: '15px' }}>❌ Before (Nested Loops):</h4>
                    <pre style={{
                      background: 'rgba(0, 0, 0, 0.5)',
                      padding: '20px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      border: '1px solid rgba(231, 76, 60, 0.3)',
                      color: '#f8f8f2'
                    }}>{`def matrix_multiply(A, B):
    rows_A, cols_A = len(A), len(A[0])
    rows_B, cols_B = len(B), len(B[0])
    
    # Create result matrix
    C = [[0 for _ in range(cols_B)] 
         for _ in range(rows_A)]
    
    # Triple nested loop - O(n³)
    for i in range(rows_A):
        for j in range(cols_B):
            for k in range(cols_A):
                C[i][j] += A[i][k] * B[k][j]
    
    return C

# Performance on 1000x1000 matrices:
# - Time: 45.2 seconds
# - Memory: 512MB peak usage`}</pre>
                  </div>

                  {/* After */}
                  <div>
                    <h4 style={{ color: '#27AE60', marginBottom: '15px' }}>✅ After (Vectorized + Optimized):</h4>
                    <pre style={{
                      background: 'rgba(0, 0, 0, 0.5)',
                      padding: '20px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      border: '2px solid rgba(39, 174, 96, 0.3)',
                      color: '#f8f8f2'
                    }}>{`import numpy as np
from numba import jit

@jit(nopython=True, parallel=True)
def matrix_multiply_optimized(A, B):
    # Use NumPy's optimized BLAS routines
    # Automatically parallelized and vectorized
    return np.dot(A, B)

def matrix_multiply_chunked(A, B, chunk_size=256):
    """Memory-efficient chunked multiplication"""
    rows_A, cols_A = A.shape
    cols_B = B.shape[1]
    
    # Process in memory-efficient chunks
    C = np.zeros((rows_A, cols_B), dtype=A.dtype)
    
    for i in range(0, rows_A, chunk_size):
        for j in range(0, cols_B, chunk_size):
            C[i:i+chunk_size, j:j+chunk_size] = np.dot(
                A[i:i+chunk_size, :],
                B[:, j:j+chunk_size]
            )
    
    return C

# Performance on 1000x1000 matrices:
# - Time: 0.12 seconds (376x faster!)
# - Memory: 64MB peak usage`}</pre>
                  </div>
                </div>

                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  borderRadius: '8px',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}>
                  <strong style={{ color: '#34d399' }}>Optimization Techniques Applied:</strong>
                  <div style={{ marginTop: '10px', fontSize: '14px' }}>
                    • Replaced nested loops with NumPy's optimized BLAS routines<br/>
                    • Added JIT compilation with Numba for near-C performance<br/>
                    • Implemented chunked processing to reduce memory usage<br/>
                    • Result: <strong style={{ color: '#fbbf24' }}>376x speed improvement, 8x less memory</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'scoring' && (
          <div>
            {/* Intelligent Scoring System */}
            <div style={{
              background: 'rgba(245, 158, 11, 0.2)',
              border: '3px solid #f59e0b',
              borderRadius: '20px',
              padding: '40px',
              position: 'relative'
            }}>
              <div style={{ 
                position: 'absolute',
                top: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#f59e0b',
                padding: '12px 30px',
                borderRadius: '25px',
                fontSize: '18px',
                fontWeight: '700'
              }}>
                📊 INTELLIGENT SCORING SYSTEM
              </div>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '40px',
                marginTop: '20px'
              }}>
                {/* Qualitative to Quantitative */}
                <div>
                  <h4 style={{ color: '#fbbf24', marginBottom: '20px', textAlign: 'center' }}>
                    Qualitative → Quantitative Scores
                  </h4>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '25px',
                    borderRadius: '12px',
                    border: '1px solid rgba(251, 191, 36, 0.3)'
                  }}>
                    <div style={{ fontSize: '15px', lineHeight: '2.2', color: '#e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>"Make it faster"</span>
                        <span style={{ color: '#34d399', fontWeight: 'bold' }}>→ Speed: 5/5</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>"Reduce memory usage"</span>
                        <span style={{ color: '#60a5fa', fontWeight: 'bold' }}>→ Memory: 4/5</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>"Too complex to read"</span>
                        <span style={{ color: '#f87171', fontWeight: 'bold' }}>→ Readability: 2/5</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>"Add error handling"</span>
                        <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>→ Reliability: 5/5</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* System Metrics */}
                <div>
                  <h4 style={{ color: '#10b981', marginBottom: '20px', textAlign: 'center' }}>
                    Real-Time System Metrics
                  </h4>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '25px',
                    borderRadius: '12px',
                    border: '1px solid rgba(16, 185, 129, 0.3)'
                  }}>
                    <div style={{ fontSize: '15px', lineHeight: '2.2', color: '#e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>Response Time:</span>
                        <span style={{ color: '#34d399', fontWeight: 'bold' }}>89ms</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>Memory Usage:</span>
                        <span style={{ color: '#60a5fa', fontWeight: 'bold' }}>128MB</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>Test Pass Rate:</span>
                        <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>94.7%</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Code Coverage:</span>
                        <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>92%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Unified Objective Function */}
              <div style={{ 
                marginTop: '30px', 
                padding: '30px', 
                background: 'rgba(0, 0, 0, 0.4)', 
                borderRadius: '16px',
                border: '2px solid rgba(245, 158, 11, 0.4)',
                textAlign: 'center'
              }}>
                <h4 style={{ color: '#fbbf24', marginBottom: '20px', fontSize: '18px' }}>
                  🎯 Unified Objective Function
                </h4>
                <div style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '16px', 
                  marginBottom: '20px',
                  color: '#60a5fa',
                  background: 'rgba(0, 0, 0, 0.5)',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid rgba(96, 165, 250, 0.3)'
                }}>
                  f(x) = w₁·speed + w₂·memory + w₃·readability + w₄·reliability
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '20px',
                  fontSize: '14px' 
                }}>
                  <div style={{
                    background: 'rgba(99, 102, 241, 0.2)',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid rgba(99, 102, 241, 0.3)'
                  }}>
                    <div style={{ color: '#c4b5fd', fontWeight: 'bold', marginBottom: '8px' }}>
                      Objective Vector:
                    </div>
                    <div style={{ color: '#e2e8f0', fontFamily: 'monospace' }}>
                      [5, 4, 2, 5, 89ms, 128MB, 94.7%]
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(16, 185, 129, 0.2)',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid rgba(16, 185, 129, 0.3)'
                  }}>
                    <div style={{ color: '#6ee7b7', fontWeight: 'bold', marginBottom: '8px' }}>
                      Optimized Score:
                    </div>
                    <div style={{ color: '#34d399', fontSize: '18px', fontWeight: 'bold' }}>
                      0.85 / 1.0
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* How It All Works Together */}
            <div style={{
              marginTop: '40px',
              background: 'rgba(139, 92, 246, 0.1)',
              borderRadius: '16px',
              padding: '30px',
              border: '1px solid rgba(139, 92, 246, 0.3)'
            }}>
              <h3 style={{ margin: '0 0 20px 0', color: '#8b5cf6', textAlign: 'center' }}>
                🔄 How It All Works Together
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px',
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                <div style={{ textAlign: 'center', padding: '15px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px', color: '#60a5fa' }}>1️⃣</div>
                  <strong style={{ color: '#60a5fa' }}>Human Input</strong>
                  <p style={{ margin: '10px 0 0 0', opacity: 0.9 }}>
                    "Make it faster", "reduce memory" → NLP converts to quantitative objectives
                  </p>
                </div>
                <div style={{ textAlign: 'center', padding: '15px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px', color: '#10b981' }}>2️⃣</div>
                  <strong style={{ color: '#10b981' }}>System Metrics</strong>
                  <p style={{ margin: '10px 0 0 0', opacity: 0.9 }}>
                    Real performance data feeds directly into optimization scoring
                  </p>
                </div>
                <div style={{ textAlign: 'center', padding: '15px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px', color: '#f59e0b' }}>3️⃣</div>
                  <strong style={{ color: '#f59e0b' }}>AI Analysis</strong>
                  <p style={{ margin: '10px 0 0 0', opacity: 0.9 }}>
                    Multiple LLMs provide weighted scores for code quality aspects
                  </p>
                </div>
                <div style={{ textAlign: 'center', padding: '15px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px', color: '#8b5cf6' }}>4️⃣</div>
                  <strong style={{ color: '#8b5cf6' }}>Evolution</strong>
                  <p style={{ margin: '10px 0 0 0', opacity: 0.9 }}>
                    Unified objective function guides evolutionary algorithm to optimize globally
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtemisOverview;